import React, { useEffect, useState } from "react";
import Login from "./components/Login";
import SharedProfilePage from "./components/SharedProfilePage";
import SkeletonPage from "./components/SkeletonPage";
import { api } from "./apiClient";
import { LOGIN_STORAGE_KEY, REMEMBER_ME_STORAGE_KEY, SETTINGS_STORAGE_KEY, TOKEN_STORAGE_KEY } from "./config";

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [session, setSession] = useState(null);
  const pathname = typeof window !== "undefined" ? window.location.pathname : "/";
  const sharedProfileMatch = pathname.match(/^\/share\/profile\/([^/]+)\/?$/);
  const invitationMatch = pathname.match(/^\/invite\/([^/]+)\/?$/);

  useEffect(() => {
    const saved = localStorage.getItem(LOGIN_STORAGE_KEY) || sessionStorage.getItem(LOGIN_STORAGE_KEY);
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) || sessionStorage.getItem(TOKEN_STORAGE_KEY);
    const rawSettings = localStorage.getItem(SETTINGS_STORAGE_KEY) || sessionStorage.getItem(SETTINGS_STORAGE_KEY);

    if (saved === "true" && token) {
      setIsLoggedIn(true);
      if (rawSettings) {
        try {
          const parsedSettings = JSON.parse(rawSettings);
          setSession({
            email: "",
            isAdmin: false,
            settings: parsedSettings,
            avatarUrl: parsedSettings?.avatarUrl || "",
          });
        } catch {
          localStorage.removeItem(SETTINGS_STORAGE_KEY);
          sessionStorage.removeItem(SETTINGS_STORAGE_KEY);
        }
      }

      api.get("/me")
        .then((response) => {
          setSession({
            ...(response.data || {}),
            avatarUrl: response.data?.settings?.avatarUrl || response.data?.avatarUrl || "",
            yourIp: response.data?.yourIp || "",
          });
          const useLocal = localStorage.getItem(REMEMBER_ME_STORAGE_KEY) === "true";
          const storage = useLocal ? localStorage : sessionStorage;
          storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(response.data.settings));
        })
        .catch(() => {
          [localStorage, sessionStorage].forEach((storage) => {
            storage.removeItem(LOGIN_STORAGE_KEY);
            storage.removeItem(TOKEN_STORAGE_KEY);
            storage.removeItem(SETTINGS_STORAGE_KEY);
            storage.removeItem(REMEMBER_ME_STORAGE_KEY);
          });
          setIsLoggedIn(false);
          setSession(null);
        });
    }
  }, []);

  const handleLogin = async (payload) => {
    const useLocal = !!payload?.rememberMe;
    const primary = useLocal ? localStorage : sessionStorage;
    const secondary = useLocal ? sessionStorage : localStorage;

    primary.setItem(LOGIN_STORAGE_KEY, "true");
    primary.setItem(TOKEN_STORAGE_KEY, payload.token);
    primary.setItem(REMEMBER_ME_STORAGE_KEY, String(useLocal));
    if (payload?.settings) {
      primary.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(payload.settings));
    }

    secondary.removeItem(LOGIN_STORAGE_KEY);
    secondary.removeItem(TOKEN_STORAGE_KEY);
    secondary.removeItem(SETTINGS_STORAGE_KEY);
    secondary.removeItem(REMEMBER_ME_STORAGE_KEY);

    setSession({
      ...(payload || {}),
      settings: payload?.settings || {},
      avatarUrl: payload?.settings?.avatarUrl || payload?.avatarUrl || "",
      yourIp: payload?.yourIp || "",
    });
    setIsLoggedIn(true);
    try {
      const response = await api.get("/me");
      setSession({
        ...(response.data || payload || {}),
        settings: response.data?.settings || payload?.settings || {},
        avatarUrl: response.data?.settings?.avatarUrl || response.data?.avatarUrl || payload?.settings?.avatarUrl || payload?.avatarUrl || "",
        yourIp: response.data?.yourIp || payload?.yourIp || "",
      });
      if (response.data?.settings) {
        primary.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(response.data.settings));
      }
    } catch {
      setSession((prev) => prev || payload || null);
    }
  };

  const handleSignOut = () => {
    [localStorage, sessionStorage].forEach((storage) => {
      storage.removeItem(LOGIN_STORAGE_KEY);
      storage.removeItem(TOKEN_STORAGE_KEY);
      storage.removeItem(SETTINGS_STORAGE_KEY);
      storage.removeItem(REMEMBER_ME_STORAGE_KEY);
    });
    setIsLoggedIn(false);
    setSession(null);
  };

  const handleSessionUpdate = (payload) => {
    setSession((prev) => ({ ...(prev || {}), ...(payload || {}) }));
    if (payload?.settings) {
      const useLocal = localStorage.getItem(REMEMBER_ME_STORAGE_KEY) === "true";
      const storage = useLocal ? localStorage : sessionStorage;
      storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(payload.settings));
    }
  };

  if (sharedProfileMatch) {
    return <SharedProfilePage token={decodeURIComponent(sharedProfileMatch[1])} />;
  }

  return isLoggedIn
    ? <SkeletonPage onSignOut={handleSignOut} session={session} onSessionUpdate={handleSessionUpdate} />
    : <Login onLogin={handleLogin} invitationToken={invitationMatch ? decodeURIComponent(invitationMatch[1]) : ""} />;
}

export default App;
